/**
 * EditableDateCell — célula editável de data na tabela de itens da contagem.
 *
 * N138: a coluna Validade era só leitura no online. O backend já aceitava
 * `data_validade` no PATCH desde sempre, e o front já convertia DD/MM/YYYY para
 * YYYY-MM-DD ao salvar — só faltava a célula deixar editar.
 *
 * ⚠️ Usa `<input type="date">` NATIVO de propósito, e não um campo de texto com
 * máscara. O navegador já valida o calendário: não deixa digitar 31/02, não
 * aceita mês 13, e abre o seletor de data. Campo de texto livre aqui seria a
 * porta de entrada para 31/02/2027 chegar no backend e voltar 400 — ou pior,
 * para um erro de digitação virar segregação errada (ver o aviso de risco no
 * comentário da coluna em useItensContagemColumns).
 *
 * Mantém o MESMO contrato do EditableNumberCell (buffer + onUpdate), porque a
 * gravação é em lote no botão Salvar, não a cada tecla.
 */
import React, { useEffect, useState } from 'react';

interface EditableDateCellProps {
  /** Data no formato de exibição DD/MM/YYYY (ou vazio). */
  value: string;
  rowIndex: number;
  fieldName: string;
  isReadOnly: boolean;
  isModified: boolean;
  /** Recebe a data de volta em DD/MM/YYYY — o formato que o save já espera. */
  onUpdate: (rowIndex: number, field: string, value: string) => void;
}

/** DD/MM/YYYY -> YYYY-MM-DD (o que o <input type="date"> entende). */
const paraInput = (br: string): string => {
  if (!br || !br.includes('/')) return '';
  const [d, m, a] = br.split('/');
  if (!d || !m || !a || a.length !== 4) return '';
  return `${a}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
};

/** YYYY-MM-DD -> DD/MM/YYYY (o formato que o payload de save já converte). */
const paraBr = (iso: string): string => {
  if (!iso || !iso.includes('-')) return '';
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
};

export const EditableDateCell: React.FC<EditableDateCellProps> = ({
  value,
  rowIndex,
  fieldName,
  isReadOnly,
  isModified,
  onUpdate,
}) => {
  const [localValue, setLocalValue] = useState(paraInput(value));

  useEffect(() => {
    setLocalValue(paraInput(value));
  }, [value]);

  if (isReadOnly) {
    return <span className="text-sm">{value || '-'}</span>;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const iso = e.target.value;
    setLocalValue(iso);
    // Campo limpo não vira update: apagar a validade sem querer mudaria a
    // segregação do item, e ninguém pediu para poder remover a data.
    if (!iso) return;
    const br = paraBr(iso);
    if (br && br !== value) {
      onUpdate(rowIndex, fieldName, br);
    }
  };

  return (
    <input
      type="date"
      value={localValue}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
      title="Alterar a validade recalcula a segregação deste item"
      className={`w-32 h-7 px-1 text-xs rounded border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        isModified
          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 font-semibold'
          : 'border-gray-300 dark:border-gray-600'
      }`}
    />
  );
};
